/**
 * Code copied from https://konvajs.org/docs/sandbox/Editable_Text.html (complex demo)
 * and adjusted accordingly
 */
import { KonvaEventObject } from "konva/lib/Core";
import { Text } from "konva/lib/shapes/Text";
import { WhiteboardShape } from "../shapes";
import { isFirefox, isSafari, px } from "../util";
import { Stage } from "konva/types/Stage";

declare global {
  interface Window {
    // needed for IE11
    readonly clipboardData: DataTransfer | null;
  }
}

export type WithInlineEditShape = WhiteboardShape & {
  destroyDynamicTextEditor(reason: UpdateReason, value?: string): void;
};

export type UpdateReason = "enter" | "esc" | "outsideClick" | "destroy" | "keydown" | "backspace";

const verticalAlignmentMap = {
  middle: "center",
  top: "baseline",
  bottom: "end",
};

export function inlineEdit(
  shape: WhiteboardShape,
  text: Text,
  options: {
    event?: "click" | "dblclick";
    autoGrow?: boolean;
    multiline?: boolean;
    enterSubmits?: boolean;
    placeholder?: string;
    textColor?: string;
    destroyOnBackspace?: boolean;
    shiftPlusEnterNewLine?: boolean; // whether to require shift+enter for new lines
    whiteboardBasicShapesEnabled?: boolean;
    onUpdate?(evt: KonvaEventObject<MouseEvent>, reason: UpdateReason, value: string): void;
  } = {}
): void {
  const myShape = shape as WithInlineEditShape;

  const destroyDynamicTextEditor = (reason: UpdateReason, value?: string) => {
    if (myShape.destroyDynamicTextEditor) {
      myShape.destroyDynamicTextEditor(reason, value);
      delete myShape.destroyDynamicTextEditor;
    }
  };

  if (!myShape.find(".has-inline-edit").length) {
    myShape.on("dragstart.inline-edit transformstart.inline-edit", () => destroyDynamicTextEditor("outsideClick"));
  }

  text.addName("has-inline-edit");

  text.on(options.event || "click", (evt) => {
    if (options.whiteboardBasicShapesEnabled) {
      handleShapeClick(evt);
      return;
    }

    let inputContainerEl: HTMLElement;
    let inputEl: HTMLInputElement | HTMLTextAreaElement;

    // if there is another input open, we should destroy it
    destroyDynamicTextEditor("outsideClick");

    const stage = myShape.getStage();
    stage.on(`resize.inline-edit${myShape.id()}`, () => {
      destroyDynamicTextEditor("outsideClick");
    });

    let originalText = text.text();
    if (originalText === options.placeholder) {
      originalText = "";
    }

    // hide the text node
    text.hide();
    myShape.draw();

    // at first lets find position of text node relative to the stage:
    const textPosition = text.absolutePosition();

    // then lets find position of stage container on the page:
    const stageBox = stage.container().getBoundingClientRect();

    const padding = 2;

    // so position of textarea will be the sum of positions above:
    const areaPosition = {
      x: textPosition.x - padding,
      y: stageBox.top + textPosition.y - padding,
    };

    if (options.multiline) {
      // create textarea over canvas with absolute position
      // first we need to find position for textarea

      // create textarea and style it
      const textarea = document.createElement("textarea");
      document.getElementById("main").appendChild(textarea);

      // apply many styles to match text on canvas as close as possible
      // remember that text rendering on canvas and on the textarea can be different
      // and sometimes it is hard to make it 100% the same. But we will try...
      if (text.text() !== options.placeholder) {
        textarea.value = text.text();
      }

      textarea.setAttribute("data-gramm", "false");
      textarea.style.position = "absolute";
      textarea.style.top = px(areaPosition.y);
      textarea.style.left = px(areaPosition.x);
      textarea.style.fontSize = px(text.fontSize() * stage.scaleX());
      textarea.style.border = "none";
      textarea.style.padding = px(padding);
      textarea.style.margin = px(0);
      textarea.style.overflow = "hidden";
      textarea.style.background = "none";
      textarea.style.outline = "none";
      textarea.style.resize = "none";
      textarea.style.lineHeight = text.lineHeight().toString();
      textarea.style.fontFamily = text.fontFamily();
      textarea.style.transformOrigin = "left top";
      textarea.style.textAlign = text.align();
      textarea.style.color = options.textColor || "#51617A";
      textarea.style.fontWeight = (text.fontStyle() || "").includes("bold") ? "bold" : "normal";
      textarea.style.fontStyle = (text.fontStyle() || "").includes("italic") ? "italic" : "normal";
      textarea.style.textDecoration = text.textDecoration();

      let transform = "";

      const rotation = text.rotation();
      if (rotation) {
        transform += `rotateZ(${rotation}deg)`;
      }

      // adjust `y` position for browsers
      let y = -1;
      if (isFirefox()) {
        y = -1 * Math.round(text.fontSize() / 20);
      } else if (isSafari()) {
        y = 0;
      }
      transform += `translateY(${px(y)})`;
      textarea.style.transform = transform;

      const setTextareaWidth = () => {
        textarea.style.width = px((text.width() - text.padding() * 2 + padding * 2 + 1) * stage.scaleX());
      };
      setTextareaWidth();

      const setTextareaHeight = () => {
        // reset height
        textarea.style.height = "auto";
        // after browsers resized it we can set actual value
        if (options.autoGrow && options.multiline !== false) {
          textarea.style.height = px(textarea.scrollHeight + 3);
        } else {
          textarea.style.height = px(text.height());
        }
      };
      setTextareaHeight();

      // set cursor at the end
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);

      textarea.addEventListener("keydown", (e) => {
        if (e.keyCode === 27) {
          destroyDynamicTextEditor("esc", originalText);
          return;
        }

        if (e.keyCode === 8 && !textarea.value && options.destroyOnBackspace) {
          destroyDynamicTextEditor("backspace", textarea.value);
          return;
        }

        if (e.keyCode === 13) {
          if (options.enterSubmits) {
            e.preventDefault();
            destroyDynamicTextEditor("enter");
            return;
          } else {
            // hide on enter, but don't hide on shift + enter (depending on options)
            if (options.shiftPlusEnterNewLine && !e.shiftKey) {
              destroyDynamicTextEditor("enter");
              return;
            }
          }
        }

        if (options.onUpdate) {
          // use setTimeout as the text needs to be updated in the textarea
          setTimeout(() => options.onUpdate(evt, "keydown", textarea.value));
        }
      });

      if (options.autoGrow) {
        textarea.addEventListener("input", () => {
          if (options.multiline === false) {
            setTextareaWidth();
          } else {
            setTextareaHeight();
          }
        });
      }

      if (options.enterSubmits) {
        textarea.addEventListener("paste", (event) => {
          let pasted = (event.clipboardData || window.clipboardData).getData("text");
          // remove newlines
          pasted = pasted.replace(/\r?\n|\r/g, " ");

          const startPos = textarea.selectionStart;
          const endPos = textarea.selectionEnd;
          textarea.value = textarea.value.substring(0, startPos) + pasted + textarea.value.substring(endPos, textarea.value.length);

          event.preventDefault();
        });
      }

      inputEl = textarea;
      inputContainerEl = textarea;
    } else {
      // adopted from https://codepen.io/shshaw/pen/c72208b279d750f1ab46b6a45bbd04d7
      const inputContainer = document.createElement("label");
      inputContainer.classList.add("inline-edit-container");
      inputContainer.style.position = "absolute";
      inputContainer.style.top = px(areaPosition.y);
      inputContainer.style.left = px(areaPosition.x);
      inputContainer.style.fontSize = px(text.fontSize() * stage.scaleX());
      inputContainer.style.padding = px(0);
      inputContainer.style.margin = px(0);
      inputContainer.style.border = "none";
      inputContainer.style.lineHeight = text.lineHeight().toString();
      inputContainer.style.fontFamily = text.fontFamily();
      inputContainer.style.color = options.textColor || "#51617A";
      inputContainer.style.fontWeight = (text.fontStyle() || "").includes("bold") ? "bold" : "normal";
      inputContainer.style.fontStyle = (text.fontStyle() || "").includes("italic") ? "italic" : "normal";
      inputContainer.style.transformOrigin = "left top";
      document.getElementById("main").appendChild(inputContainer);

      const input = document.createElement("input");
      if (text.text() !== options.placeholder) {
        input.value = text.text();
      }
      input.setAttribute("data-gramm", "false");
      input.type = "text";
      input.size = 1;
      input.style.padding = px(0);
      input.style.margin = px(0);
      input.style.overflow = "hidden";
      input.style.background = "none";
      input.style.textDecoration = text.textDecoration();
      input.style.textAlign = text.align();
      inputContainer.appendChild(input);

      let transform = "";
      const rotation = text.rotation();
      if (rotation) {
        transform += `rotateZ(${rotation}deg)`;
      }
      // adjust `y` position for browsers
      let y = -1;
      let x = 0;
      if (isFirefox()) {
        y = Math.round(text.fontSize() / 18);
        x = Math.round(text.fontSize() / 18);
      } else if (isSafari()) {
        y = 0;
      }
      transform += `translate(${px(x)}, ${px(y)})`;
      inputContainer.style.transform = transform;

      input.focus();

      if (options.autoGrow) {
        const setInputValueAsDataAttrToParent = () => ((input.parentNode as unknown as HTMLOrSVGElement).dataset.value = input.value);
        input.addEventListener("input", setInputValueAsDataAttrToParent);
        setInputValueAsDataAttrToParent();
      }

      input.addEventListener("keydown", (e) => handleInputKeydown(e, originalText, input, evt));

      inputEl = input;
      inputContainerEl = inputContainer;
    }

    const handleOutsideClick = (e: MouseEvent) => {
      if (e.target !== inputEl) {
        destroyDynamicTextEditor("outsideClick", inputEl.value);
      }
    };
    setTimeout(() => window.addEventListener("click", handleOutsideClick));

    myShape.destroyDynamicTextEditor = (reason: UpdateReason, value?: string) => {
      stage.off(`resize.inline-edit${myShape.id()}`);

      window.removeEventListener("click", handleOutsideClick);

      if (inputContainerEl.parentNode) {
        inputContainerEl.parentNode.removeChild(inputContainerEl);
      }

      if (reason !== "destroy") {
        if (options.onUpdate) {
          value = value || inputEl.value;
          options.onUpdate(evt, reason, value);
        }

        text.show();
        myShape.getLayer().batchDraw();
      }
    };
  });

  const styleInnerDiv = (innerDiv: HTMLDivElement): void => {
    innerDiv.setAttribute("data-gramm", "false");
    innerDiv.setAttribute("data-placeholder", options.placeholder);
    innerDiv.setAttribute("contenteditable", "true");
    innerDiv.style.display = "block";
    innerDiv.style.border = "none";
    innerDiv.style.overflow = "hidden";
    innerDiv.style.background = "none";
    innerDiv.style.outline = "none";
    innerDiv.style.resize = "none";
    innerDiv.style.lineHeight = text.lineHeight().toString();
  };

  const styleOuterDiv = (outerDiv: HTMLDivElement, areaPosition: { x: number; y: number }, stage: Stage, padding: number): void => {
    outerDiv.style.position = "absolute";
    outerDiv.style.display = "flex";
    outerDiv.style.alignItems = verticalAlignmentMap[text.verticalAlign()];
    outerDiv.style.top = px(areaPosition.y);
    outerDiv.style.left = px(areaPosition.x);
    outerDiv.style.fontSize = px(text.fontSize() * stage.scaleX());
    outerDiv.style.padding = px(padding);
    outerDiv.style.margin = px(0);
  };

  const styleParagraph = (paragraph: HTMLParagraphElement, stage: Stage): void => {
    paragraph.style.lineHeight = text.lineHeight().toString();
    paragraph.style.fontFamily = text.fontFamily();
    paragraph.style.transformOrigin = "left top";
    paragraph.style.textAlign = text.align();
    paragraph.style.color = options.textColor || "#51617A";
    paragraph.style.fontWeight = (text.fontStyle() || "").includes("bold") ? "bold" : "normal";
    paragraph.style.fontStyle = (text.fontStyle() || "").includes("italic") ? "italic" : "normal";
    paragraph.style.textDecoration = text.textDecoration();
    paragraph.style.fontSize = px(text.fontSize() * stage.scaleX());
    paragraph.style.margin = px(0);
  };

  const styleSpan = (span: HTMLSpanElement): void => {
    span.style.height = px(14);
    span.style.minWidth = px(1);
    span.style.display = "inline-block";
  };

  const setCursorAtParagraphEnd = (paragraph: HTMLParagraphElement): void => {
    const selection = window.getSelection();
    selection.selectAllChildren(paragraph);
    selection.collapseToEnd();
    paragraph.focus();
    paragraph.scrollTop = paragraph.scrollHeight;
    paragraph.scrollIntoView(false);
  };

  const handleInputKeydown = (e: KeyboardEvent, originalText: string, input: HTMLInputElement, evt: KonvaEventObject<MouseEvent>): void => {
    if (e.keyCode === 27) {
      destroyDynamicTextEditor("esc", originalText);
      return;
    }

    if (e.keyCode === 8 && !input.value && options.destroyOnBackspace) {
      destroyDynamicTextEditor("backspace", input.value);
      return;
    }

    if (e.keyCode === 13) {
      destroyDynamicTextEditor("enter");
      return;
    }

    if (options.onUpdate) {
      // use setTimeout as the text needs to be updated in the textarea
      setTimeout(() => options.onUpdate(evt, "keydown", input.value));
    }
  };

  const handleShapeClick = (evt: KonvaEventObject<MouseEvent>): void => {
    let inputContainerEl: HTMLElement;
    let inputEl: HTMLInputElement | HTMLDivElement;

    // if there is another input open, we should destroy it
    destroyDynamicTextEditor("outsideClick");

    const stage = myShape.getStage();
    stage.on(`resize.inline-edit${myShape.id()}`, () => {
      destroyDynamicTextEditor("outsideClick");
    });

    let originalText = text.text();
    if (originalText === options.placeholder) {
      originalText = "";
    }

    // hide the text node
    text.hide();
    myShape.draw();

    // at first lets find position of text node relative to the stage:
    const textPosition = text.absolutePosition();

    // then lets find position of stage container on the page:
    const stageBox = stage.container().getBoundingClientRect();

    const padding = 2;

    // so position of textarea will be the sum of positions above:
    const areaPosition = {
      x: textPosition.x - padding,
      y: stageBox.top + textPosition.y - padding,
    };

    if (options.multiline) {
      // create div over canvas with absolute position
      // first we need to find position for it

      // create div and style it
      const outerDiv = document.createElement("div");
      const innerDiv = document.createElement("div");
      const paragraph = document.createElement("p");
      const span = document.createElement("span");

      paragraph.append(span);
      innerDiv.append(paragraph);
      outerDiv.append(innerDiv);
      document.getElementById("main").appendChild(outerDiv);

      if (text.text() !== options.placeholder) {
        paragraph.innerText = text.text();
      }

      styleInnerDiv(innerDiv);
      styleOuterDiv(outerDiv, areaPosition, stage, padding);
      styleParagraph(paragraph, stage);
      styleSpan(span);

      const setWidth = () => {
        outerDiv.style.width = px((text.width() - text.padding() * 2 + padding * 2 + 1) * stage.scaleX());
        innerDiv.style.width = px((text.width() - text.padding() * 2 + padding * 2 + 1) * stage.scaleX());
      };
      setWidth();

      const setHeight = () => {
        outerDiv.style.height = px(text.height() * stage.scaleX());

        innerDiv.style.minHeight = px(12 * stage.scaleX());
        innerDiv.style.maxHeight = px(text.height() * stage.scaleX());

        paragraph.style.minHeight = px(12 * stage.scaleX());
      };
      setHeight();

      setCursorAtParagraphEnd(paragraph);

      outerDiv.addEventListener("keydown", (e) => {
        if (e.keyCode === 27) {
          destroyDynamicTextEditor("esc", originalText);
          return;
        }

        if (e.keyCode === 8 && !paragraph.innerText && options.destroyOnBackspace) {
          destroyDynamicTextEditor("backspace", paragraph.innerText);
          return;
        }

        if (e.keyCode === 13) {
          if (options.enterSubmits) {
            e.preventDefault();
            destroyDynamicTextEditor("enter");
            return;
          } else {
            // hide on enter, but don't hide on shift + enter (depending on options)
            if (options.shiftPlusEnterNewLine && !e.shiftKey) {
              destroyDynamicTextEditor("enter");
              return;
            }
          }
          document.execCommand("insertLineBreak");
          e.preventDefault();
        }

        if (options.onUpdate) {
          // use setTimeout as the text needs to be updated in the textarea
          setTimeout(() => options.onUpdate(evt, "keydown", paragraph.innerText));
        }
      });

      if (options.autoGrow) {
        outerDiv.addEventListener("input", () => {
          if (options.multiline === false) {
            setWidth();
          } else {
            setHeight();
          }
        });
      }

      inputEl = outerDiv;
      inputContainerEl = outerDiv;
    } else {
      // adopted from https://codepen.io/shshaw/pen/c72208b279d750f1ab46b6a45bbd04d7
      const inputContainer = document.createElement("label");
      inputContainer.classList.add("inline-edit-container");
      inputContainer.style.position = "absolute";
      inputContainer.style.top = px(areaPosition.y);
      inputContainer.style.left = px(areaPosition.x);
      inputContainer.style.fontSize = px(text.fontSize() * stage.scaleX());
      inputContainer.style.padding = px(0);
      inputContainer.style.margin = px(0);
      inputContainer.style.border = "none";
      inputContainer.style.lineHeight = text.lineHeight().toString();
      inputContainer.style.fontFamily = text.fontFamily();
      inputContainer.style.color = options.textColor || "#51617A";
      inputContainer.style.fontWeight = (text.fontStyle() || "").includes("bold") ? "bold" : "normal";
      inputContainer.style.fontStyle = (text.fontStyle() || "").includes("italic") ? "italic" : "normal";
      inputContainer.style.transformOrigin = "left top";
      document.getElementById("main").appendChild(inputContainer);

      const input = document.createElement("input");
      if (text.text() !== options.placeholder) {
        input.value = text.text();
      }
      input.setAttribute("data-gramm", "false");
      input.type = "text";
      input.size = 1;
      input.style.padding = px(0);
      input.style.margin = px(0);
      input.style.overflow = "hidden";
      input.style.background = "none";
      input.style.textDecoration = text.textDecoration();
      input.style.textAlign = text.align();
      inputContainer.appendChild(input);

      let transform = "";
      const rotation = text.rotation();
      if (rotation) {
        transform += `rotateZ(${rotation}deg)`;
      }
      // adjust `y` position for browsers
      let y = -1;
      let x = 0;
      if (isFirefox()) {
        y = Math.round(text.fontSize() / 18);
        x = Math.round(text.fontSize() / 18);
      } else if (isSafari()) {
        y = 0;
      }
      transform += `translate(${px(x)}, ${px(y)})`;
      inputContainer.style.transform = transform;

      input.focus();

      if (options.autoGrow) {
        const setInputValueAsDataAttrToParent = () => ((input.parentNode as unknown as HTMLOrSVGElement).dataset.value = input.value);
        input.addEventListener("input", setInputValueAsDataAttrToParent);
        setInputValueAsDataAttrToParent();
      }

      input.addEventListener("keydown", (e) => handleInputKeydown(e, originalText, input, evt));

      inputEl = input;
      inputContainerEl = inputContainer;
    }

    const handleOutsideClick = (e: MouseEvent) => {
      if (e.target !== inputEl) {
        destroyDynamicTextEditor("outsideClick", inputEl.innerText);
      }
    };
    setTimeout(() => window.addEventListener("click", handleOutsideClick));

    myShape.destroyDynamicTextEditor = (reason: UpdateReason, value?: string) => {
      stage.off(`resize.inline-edit${myShape.id()}`);

      window.removeEventListener("click", handleOutsideClick);

      if (inputContainerEl.parentNode) {
        inputContainerEl.parentNode.removeChild(inputContainerEl);
      }

      if (reason !== "destroy") {
        if (options.onUpdate) {
          value = value || inputEl.innerText;
          options.onUpdate(evt, reason, value);
        }

        text.show();
        myShape.getLayer().batchDraw();
      }
    };
  };

  // when destroying the shape we want to remove the dynamic text input
  const oldDestroy = text.destroy;
  text.destroy = function (): Text {
    destroyDynamicTextEditor("destroy");
    return oldDestroy.call(text);
  };
}
